import { clearApiClientCache } from "@/api";
import DeleteModal from "@/components/modal/DeleteModal.component";
import DataProcessingAgreementForm from "@/components/org-management/DataProcessingAgreementForm.component";
import OrgDataForm from "@/components/org-management/OrgDataForm.component";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { deleteOrg } from "@/models/org";
import { OrgModule } from "@/store/modules/org";
import { UserModule } from "@/store/modules/user";
import { userDataLoader } from "@/utils/auth-service";
import { persistanceService } from "@/utils/persistance-service";
import { Component } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BContainer,
  BRow,
} from "@/lib/typed-bootstrap";

@Component
export default class OrgSettings extends UserPermissions {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  form: any = null;
  errorMessage = "";
  confirmName = "";
  isLoading = false;

  get orgName() {
    return OrgModule.name;
  }

  get showErrorMessage(): boolean {
    return !!this.errorMessage;
  }

  get deleteTitle(): string {
    return this.$t("warning_delete_org_title", {
      name: this.orgName,
    }).toString();
  }

  get deleteInfo(): string {
    return this.$t("warning_delete_org_info", {
      name: this.orgName,
    }).toString();
  }

  get deleteInstruction(): string {
    return this.$t("warning_delete_org_instruction").toString();
  }

  async onDelete(): Promise<void> {
    try {
      if (!this.canManageOrg) {
        throw new Error(this.$t("error_org_deletion_requires_role").toString());
      }
      if (this.orgName === this.confirmName) {
        await deleteOrg(
          OrgModule.id,
          OrgModule.billingAccount?.id ?? "",
          OrgModule.billingAccount?.paymentProviderId ?? "",
        );
        this.clearCache();
        await UserModule.onUserAction(async () => {
          userDataLoader.refreshToken();
          await userDataLoader.loading;
        });

        const nextOrgId = Object.keys(UserModule.roles)[0];
        if (nextOrgId) {
          this.$router.push({
            name: "org-api-dashboard",
            params: { orgId: nextOrgId },
          });
        } else {
          OrgModule.reset();
          this.$router.push({
            name: "workspace-setup",
          });
        }
      } else {
        throw new Error(this.$t("error_org_deletion_confirm").toString());
      }
    } catch (err) {
      this.errorMessage = (err as Error).message;
    }
  }

  clearCache() {
    persistanceService.clearWorkspace();
    clearApiClientCache();
    UserModule.handleDeletionForOrgWithId(OrgModule.id);
  }

  openDeleteModal() {
    this.$bvModal.show("delete-modal");
  }

  render() {
    return (
      <BContainer class="default__view-setup">
        <OrgDataForm />
        <DataProcessingAgreementForm />
        <BAlert
          show={this.showErrorMessage}
          variant="danger"
          data-testid="deletion-error-alert"
          class="default__section"
        >
          {this.errorMessage}
        </BAlert>

        {this.canManageOrg && (
          <div class="default__section" data-testid="delete-org">
            <div class="default__section-header danger__zone">
              {this.$t("dangerzone")}
            </div>

            <BRow class="default__section">
              <BCard
                tag="article"
                bodyClass="danger__zone-banner-body"
                class="danger__zone-banner"
              >
                <BRow>
                  <BCol md="8">
                    <div class="danger__zone-headline">
                      {this.$t("dangerzone_workspace_hint")}
                    </div>
                    <div class="danger__zone-subline">
                      <i18n path="dangerzone_support">
                        <template slot="contact">
                          <a
                            role="button"
                            class="get-started__button cta__align-elastic"
                            href="mailto:hello@graphapi.com"
                          >
                            {this.$t("global_contact_us")}
                          </a>
                        </template>
                      </i18n>
                    </div>
                  </BCol>

                  <BCol md="4" class="text-right">
                    <BButton
                      id="dangerZoneDeleteButton"
                      class="danger__zone-delete-button"
                      data-testid="delete-org-button"
                      onClick={this.openDeleteModal}
                    >
                      {this.$t("global_cta_delete_org").toString()}
                    </BButton>

                    <DeleteModal
                      onConfirmDelete={this.onDelete}
                      onUpdate={(val: string) => (this.confirmName = val)}
                      name={this.orgName ?? ""}
                      title={this.deleteTitle}
                      info={this.deleteInfo}
                      instruction={this.deleteInstruction}
                    />
                  </BCol>
                </BRow>
              </BCard>
            </BRow>
          </div>
        )}
      </BContainer>
    );
  }
}
